import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, AlertTriangle, Copy, Download } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { toast } from "sonner";
import { http, apiError, API } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { Card, SectionTitle, SeverityChip, StatusBadge, Confidence, Spinner } from "@/components/Kit";
import { PostureDial, GradeChip, VerifyChip, SignificanceChip } from "@/components/WebKit";

const TABS = ["Overview", "Findings", "Integrity Timeline", "Cloaking", "Evidence", "Baselines"];
const MODULE_LABEL = { http: "HTTP / Redirect", dns_tls: "DNS · TLS · Headers", fingerprint_cve: "Tech & CVE",
  integrity: "Defacement / SEO", cloaking: "Cloaking", ioc: "Outbound / IOC", active: "Active (ZAP)" };

export default function WebTargetDetail() {
  const { id } = useParams();
  const { orgId } = useOrg();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [trend, setTrend] = useState([]);

  const load = useCallback(() => http.get(`/web/targets/${id}`, { params: { org_id: orgId } }).then((r) => setT(r.data)), [id, orgId]);
  useEffect(() => {
    if (!orgId) return;
    load();
    http.get(`/web/targets/${id}/posture-trend`, { params: { org_id: orgId } }).then((r) => setTrend(r.data));
  }, [id, orgId, load]);

  if (!t) return <div className="p-8 text-[#8A8175]">Loading target…</div>;

  const approveBaseline = async () => {
    try { await http.post(`/web/targets/${id}/baseline/approve`, {}, { params: { org_id: orgId, approved_by: "analyst" } });
      toast.success("Baseline approved"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 max-w-[1500px] mx-auto fade-up">
      <button onClick={() => navigate("/web/targets")} className="flex items-center gap-1 text-xs text-[#8A8175] hover:text-[#A5300F] mb-3">
        <ArrowLeft className="w-3 h-3" /> Web Targets
      </button>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#323232]">{t.hostname}</h1>
          <div className="flex items-center gap-3 mt-1 text-sm text-[#8A8175]">
            <VerifyChip verified={t.authorisation?.verified} active={t.auth_active} />
            <span>·</span><span>{t.label}</span>
            {t.authorisation?.expires_at && <><span>·</span><span>Auth until {new Date(t.authorisation.expires_at).toLocaleDateString("en-GB")}</span></>}
          </div>
        </div>
        <button onClick={() => navigate(`/web/scan?target_id=${id}`)} data-testid="scan-target-btn"
          className="bg-[#A5300F] text-white font-semibold text-sm px-4 py-2 rounded-md hover:bg-[#8F2A0D]">New Scan</button>
      </div>

      {!t.auth_active && (
        <div className="flex items-center gap-2 bg-[#FFF5EB] border border-[#E7D9C6] text-[#8a5a12] text-sm rounded-md px-4 py-2.5 mb-4">
          <AlertTriangle className="w-4 h-4" /> Authorisation has expired. Scanning is blocked until it is renewed.
        </div>
      )}

      <div className="flex gap-1 border-b border-[#ECE6DD] mb-5">
        {TABS.map((x) => (
          <button key={x} onClick={() => setTab(x)} data-testid={`tab-${x.replace(/\s/g, "-").toLowerCase()}`}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === x ? "border-[#A5300F] text-[#A5300F]" : "border-transparent text-[#8A8175] hover:text-[#A5300F]"}`}>{x}</button>
        ))}
      </div>

      {tab === "Overview" && <Overview t={t} trend={trend} onApprove={approveBaseline} />}
      {tab === "Findings" && <FindingsTab id={id} orgId={orgId} navigate={navigate} />}
      {tab === "Integrity Timeline" && <TimelineTab id={id} orgId={orgId} />}
      {tab === "Cloaking" && <CloakingTab id={id} orgId={orgId} />}
      {tab === "Evidence" && <EvidenceTab t={t} orgId={orgId} />}
      {tab === "Baselines" && <BaselinesTab id={id} orgId={orgId} onApprove={approveBaseline} />}
    </div>
  );
}

function Overview({ t, trend, onApprove }) {
  const lastScan = (t.scans || [])[0];
  const modules = ["http", "dns_tls", "fingerprint_cve", "integrity", "cloaking", "ioc"];
  const run = new Set(lastScan?.modules_run || []);
  const skipped = Object.fromEntries((lastScan?.modules_skipped || []).map((s) => [s.module, s.reason]));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Card className="p-6 flex flex-col items-center justify-center">
        <SectionTitle className="self-start">Posture Score</SectionTitle>
        <div className="my-3"><PostureDial score={t.posture_score} grade={t.posture_grade} /></div>
        {lastScan?.top_deductions?.length > 0 && (
          <div className="w-full text-xs mt-2">
            <div className="text-[10px] uppercase tracking-wider text-[#8A8175] font-semibold mb-1">Top deductions</div>
            {lastScan.top_deductions.map((d, i) => (
              <div key={`${d.title}-${i}`} className="flex justify-between border-b border-[#F4EFE8] py-1">
                <span className="truncate mr-2">{d.title}</span>
                <span className="font-mono text-[#A5300F]">−{d.deduction}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-5 lg:col-span-2">
        <SectionTitle>Posture — 90-day Trend</SectionTitle>
        {trend.length === 0 ? <div className="py-10 text-center text-[#B5AC9E] text-sm">No completed scans yet.</div> : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={trend.map((p) => ({ ...p, date: (p.date || "").slice(0, 10) }))} margin={{ top: 12, right: 16, left: -12 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0EAE1" />
              <XAxis dataKey="date" tick={{ fill: "#8A8175", fontSize: 10 }} />
              <YAxis domain={[0, 100]} tick={{ fill: "#8A8175", fontSize: 10 }} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #ECE6DD", borderRadius: 8, fontSize: 12 }} />
              <Line type="monotone" dataKey="score" stroke="#A5300F" strokeWidth={2} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card className="p-5 lg:col-span-2">
        <SectionTitle>Module Status</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
          {modules.map((m) => (
            <div key={m} className="border border-[#ECE6DD] rounded-md p-3" data-testid={`module-card-${m}`}>
              <div className="text-sm font-semibold">{MODULE_LABEL[m]}</div>
              {skipped[m] ? <span className="inline-block mt-1 text-[10px] font-bold text-[#E39629] uppercase">Skipped</span>
                : run.has(m) ? <span className="inline-flex items-center gap-1 mt-1 text-[11px] text-[#2E7D32] font-semibold"><CheckCircle2 className="w-3 h-3" /> Ran</span>
                : <span className="text-[11px] text-[#B5AC9E] mt-1 inline-block">Not in last scan</span>}
              {skipped[m] && <div className="text-[10px] text-[#8A8175] mt-1">{skipped[m]}</div>}
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5">
        <SectionTitle>Baseline</SectionTitle>
        {t.current_baseline ? (
          <div className="text-sm mt-2 space-y-1">
            <div className="text-[#2E7D32] font-semibold flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Approved baseline current</div>
            <KV k="URL" v={t.current_baseline.url} />
            <KV k="Approved by" v={t.current_baseline.approved_by} />
            <KV k="Captured" v={new Date(t.current_baseline.captured_at).toLocaleString("en-GB")} />
          </div>
        ) : (
          <div className="text-sm mt-2 space-y-2">
            <div className="text-[#A5300F]">No approved baseline. Integrity comparison is inactive until one is approved.</div>
            <button onClick={onApprove} data-testid="approve-baseline-btn" className="text-sm bg-[#A5300F] text-white font-semibold px-3 py-1.5 rounded-md hover:bg-[#8F2A0D]">Approve latest candidate</button>
          </div>
        )}
      </Card>

      <Card className="p-5 lg:col-span-3">
        <SectionTitle>Scan History</SectionTitle>
        <table className="w-full text-sm mt-3">
          <thead><tr className="text-[11px] uppercase tracking-wider text-[#8A8175] text-left border-b border-[#ECE6DD]">
            <th className="py-2">Profile</th><th>Status</th><th>Grade</th><th>Findings</th><th>Modules run</th><th>Finished</th>
          </tr></thead>
          <tbody>
            {(t.scans || []).map((s) => (
              <tr key={s.id} className="border-b border-[#F4EFE8]" data-testid={`scan-row-${s.id}`}>
                <td className="py-2 font-semibold capitalize">{s.scan_type}</td>
                <td><span className="capitalize">{s.status}</span></td>
                <td><GradeChip grade={s.posture_grade} score={s.posture_score} /></td>
                <td className="stat-numeral">{s.summary?.total ?? 0}</td>
                <td className="text-xs text-[#8A8175]">{(s.modules_run || []).join(", ") || "—"}</td>
                <td className="text-xs text-[#8A8175]">{s.finished_at ? new Date(s.finished_at).toLocaleString("en-GB") : "—"}</td>
              </tr>
            ))}
            {(t.scans || []).length === 0 && <tr><td colSpan={6} className="py-6 text-center text-[#B5AC9E]">No scans yet.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function FindingsTab({ id, orgId, navigate }) {
  const [data, setData] = useState({ items: [] });
  const [sev, setSev] = useState("");
  useEffect(() => {
    const params = { org_id: orgId, target_id: id };
    if (sev) params.severity = sev;
    http.get("/web/findings", { params }).then((r) => setData(r.data));
  }, [id, orgId, sev]);
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 p-3 border-b border-[#ECE6DD] text-sm">
        <span className="text-[#8A8175]">Severity</span>
        <select value={sev} onChange={(e) => setSev(e.target.value)} data-testid="web-finding-sev-filter" className="border border-[#E7D9C6] rounded px-2 py-1 text-sm">
          <option value="">All</option>{["Critical", "High", "Medium", "Low", "Info"].map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      <table className="w-full text-sm" data-testid="web-findings-table">
        <thead><tr className="text-[10px] uppercase tracking-wider text-[#8A8175] text-left bg-[#FFF5EB] border-b border-[#ECE6DD]">
          <th className="py-2 px-3">Severity</th><th>Module</th><th>Title</th><th>Confidence</th><th>CVE</th><th className="text-right">Age</th><th>Status</th>
        </tr></thead>
        <tbody>
          {data.items.map((f) => (
            <tr key={f.id} onClick={() => navigate(`/web/findings/${f.id}`)} className="border-b border-[#F4EFE8] hover:bg-[#FBF7F1] cursor-pointer" data-testid={`web-finding-${f.id}`}>
              <td className="py-2 px-3"><SeverityChip severity={f.severity} small /></td>
              <td className="text-xs text-[#8A8175]">{f.module}</td>
              <td className="font-semibold max-w-md truncate">{f.title}</td>
              <td><Confidence label={f.confidence} /></td>
              <td className="text-xs font-mono">{(f.cve_ids || [])[0] || "—"}</td>
              <td className="text-right text-xs">{f.age_days}d</td>
              <td><StatusBadge status={f.status} /></td>
            </tr>
          ))}
          {data.items.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-[#B5AC9E]">No findings.</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

function TimelineTab({ id, orgId }) {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => { http.get(`/web/targets/${id}/change-events`, { params: { org_id: orgId } }).then((r) => setEvents(r.data)); }, [id, orgId]);
  return (
    <Card className="p-5">
      <SectionTitle>Integrity Timeline</SectionTitle>
      <p className="text-sm text-[#8A8175] mt-1 mb-4">Every detected change against the approved baseline, newest first.</p>
      {events.length === 0 && <div className="py-8 text-center text-[#B5AC9E] text-sm">No change events recorded. Approve a baseline to begin tracking.</div>}
      <div className="space-y-2">
        {events.map((e) => (
          <div key={e.id} className="border border-[#ECE6DD] rounded-md" data-testid={`change-event-${e.id}`}>
            <button onClick={() => setOpen(open === e.id ? null : e.id)} className="w-full flex items-center justify-between px-4 py-2.5 text-left">
              <div className="flex items-center gap-3">
                <SignificanceChip level={e.significance} />
                <span className="text-sm font-semibold capitalize">{e.change_type}</span>
                <span className="text-xs text-[#8A8175]">{(e.fields_changed || []).join(", ")}</span>
              </div>
              <span className="text-xs text-[#8A8175]">{new Date(e.detected_at).toLocaleString("en-GB")}</span>
            </button>
            {open === e.id && (
              <div className="grid grid-cols-2 gap-4 px-4 pb-4 text-xs">
                <div>
                  <div className="text-[10px] uppercase text-[#8A8175] font-semibold mb-1">Previous</div>
                  <pre className="bg-[#F6F1EA] border border-[#ECE6DD] rounded p-2 whitespace-pre-wrap break-all">{JSON.stringify(e.previous_value, null, 2)}</pre>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-[#8A8175] font-semibold mb-1">Current</div>
                  <pre className="bg-[#FFF5EB] border border-[#E7D9C6] rounded p-2 whitespace-pre-wrap break-all">{JSON.stringify(e.current_value, null, 2)}</pre>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

function CloakingTab({ id, orgId }) {
  const [d, setD] = useState(null);
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  useEffect(() => { http.get(`/web/targets/${id}/cloaking`, { params: { org_id: orgId } }).then((r) => setD(r.data)); }, [id, orgId]);
  if (!d) return <div className="text-[#8A8175]">Loading…</div>;
  const rows = (d.matrix || []).filter((r) => r.ok);
  const cols = [["status", "Status"], ["final_reg", "Final domain"], ["title", "Title"], ["content_length", "Length"], ["spam_terms", "Spam"]];
  const base = rows.find((r) => r.profile === "browser");
  const cell = (r, k) => k === "spam_terms" ? (r.spam_terms || []).join(", ") : String(r[k] ?? "—");
  const divergent = (r, k) => base && r.profile !== "browser" && k !== "spam_terms" && String(r[k] ?? "") !== String(base[k] ?? "");
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 bg-[#FFF5EB] border border-[#E7D9C6] text-[#8a5a12] text-xs rounded-md px-4 py-2.5" data-testid="cloaking-limitation">
        <AlertTriangle className="w-4 h-4 shrink-0" /> {d.limitation}
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="cloaking-matrix">
          <thead><tr className="text-[10px] uppercase tracking-wider text-[#8A8175] text-left bg-[#FFF5EB] border-b border-[#ECE6DD]">
            <th className="py-2 px-3">Profile</th>{cols.map(([, l]) => <th key={l}>{l}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.profile} className="border-b border-[#F4EFE8]">
                <td className="py-2 px-3 font-semibold">{r.profile}</td>
                {cols.map(([k]) => (
                  <td key={k} className={`text-xs max-w-[180px] truncate ${divergent(r, k) ? "text-[#A5300F] font-bold bg-[#A5300F0d]" : "text-[#5C554C]"}`}>{cell(r, k)}</td>
                ))}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-[#B5AC9E]">No cloaking scan yet.</td></tr>}
          </tbody>
        </table>
      </Card>
      {rows.length > 1 && (
        <Card className="p-4">
          <SectionTitle>Text comparison</SectionTitle>
          <div className="flex gap-2 my-2 text-sm">
            <select value={a} onChange={(e) => setA(e.target.value)} className="border border-[#E7D9C6] rounded px-2 py-1">
              <option value="">Profile A</option>{rows.map((r) => <option key={r.profile}>{r.profile}</option>)}
            </select>
            <select value={b} onChange={(e) => setB(e.target.value)} className="border border-[#E7D9C6] rounded px-2 py-1">
              <option value="">Profile B</option>{rows.map((r) => <option key={r.profile}>{r.profile}</option>)}
            </select>
          </div>
          {a && b && (
            <div className="grid grid-cols-2 gap-3 text-xs">
              {[a, b].map((p) => {
                const r = rows.find((x) => x.profile === p);
                return <div key={p}><div className="font-semibold mb-1">{p}</div>
                  <pre className="bg-[#F6F1EA] border border-[#ECE6DD] rounded p-2 whitespace-pre-wrap break-all">{`title: ${r.title}\ndomain: ${r.final_reg}\nlength: ${r.content_length}\nsimhash: ${r.simhash}`}</pre></div>;
              })}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function EvidenceTab({ t, orgId }) {
  const [scanId, setScanId] = useState((t.scans || [])[0]?.id || "");
  const [arts, setArts] = useState([]);
  const [manifest, setManifest] = useState(null);
  useEffect(() => {
    if (!scanId) return;
    http.get(`/web/scans/${scanId}/evidence`, { params: { org_id: orgId } }).then((r) => setArts(r.data));
    http.get(`/web/scans/${scanId}/evidence-manifest`, { params: { org_id: orgId } }).then((r) => setManifest(r.data));
  }, [scanId, orgId]);
  const copy = (x) => { navigator.clipboard.writeText(x); toast.success("SHA-256 copied"); };
  const dl = (aid) => window.open(`${API}/web/scans/${scanId}/evidence/${aid}?org_id=${orgId}`, "_blank");
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <SectionTitle>Evidence Locker</SectionTitle>
        <div className="flex items-center gap-2 text-sm">
          <select value={scanId} onChange={(e) => setScanId(e.target.value)} data-testid="evidence-scan-select" className="border border-[#E7D9C6] rounded px-2 py-1">
            {(t.scans || []).map((s) => <option key={s.id} value={s.id}>{s.scan_type} · {s.finished_at ? new Date(s.finished_at).toLocaleString("en-GB") : s.status}</option>)}
          </select>
          {manifest && <span className="text-xs text-[#8A8175] font-mono" title="Manifest SHA-256">manifest {manifest.manifest_sha256?.slice(0, 12)}…</span>}
        </div>
      </div>
      <table className="w-full text-sm mt-3" data-testid="evidence-table">
        <thead><tr className="text-[10px] uppercase tracking-wider text-[#8A8175] text-left border-b border-[#ECE6DD]">
          <th className="py-2">Type</th><th>Filename</th><th>Size</th><th>SHA-256</th><th>Captured</th><th></th>
        </tr></thead>
        <tbody>
          {arts.map((a) => (
            <tr key={a.id} className="border-b border-[#F4EFE8]">
              <td className="py-2 text-xs">{a.artefact_type}</td>
              <td className="text-xs font-semibold">{a.filename}</td>
              <td className="text-xs">{a.size_bytes} B</td>
              <td className="text-xs font-mono flex items-center gap-1"><span className="truncate max-w-[160px]">{a.sha256}</span><button onClick={() => copy(a.sha256)}><Copy className="w-3 h-3 text-[#8A8175]" /></button></td>
              <td className="text-xs text-[#8A8175]">{new Date(a.captured_at).toLocaleString("en-GB")}</td>
              <td><button onClick={() => dl(a.id)} data-testid={`download-${a.id}`} className="text-[#A5300F]"><Download className="w-4 h-4" /></button></td>
            </tr>
          ))}
          {arts.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-[#B5AC9E]">No artefacts for this scan.</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

function BaselinesTab({ id, orgId, onApprove }) {
  const [bls, setBls] = useState([]);
  useEffect(() => { http.get(`/web/targets/${id}/baselines`, { params: { org_id: orgId } }).then((r) => setBls(r.data)); }, [id, orgId]);
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <SectionTitle>Baselines</SectionTitle>
        <button onClick={onApprove} data-testid="approve-latest-baseline" className="text-sm bg-[#A5300F] text-white font-semibold px-3 py-1.5 rounded-md hover:bg-[#8F2A0D]">Approve latest candidate</button>
      </div>
      <p className="text-xs text-[#8A8175] mt-1 mb-3">A baseline is never auto-promoted after a change — an analyst must approve it, so an active compromise is never silently absorbed.</p>
      <table className="w-full text-sm">
        <thead><tr className="text-[10px] uppercase tracking-wider text-[#8A8175] text-left border-b border-[#ECE6DD]">
          <th className="py-2">URL</th><th>Title</th><th>Captured</th><th>Approved by</th><th>Current</th>
        </tr></thead>
        <tbody>
          {bls.map((b) => (
            <tr key={b.id} className="border-b border-[#F4EFE8]">
              <td className="py-2 text-xs max-w-[220px] truncate">{b.url}</td>
              <td className="text-xs truncate max-w-[220px]">{b.title || "—"}</td>
              <td className="text-xs text-[#8A8175]">{new Date(b.captured_at).toLocaleString("en-GB")}</td>
              <td className="text-xs">{b.approved_by || "—"}</td>
              <td>{b.is_current ? <span className="text-[#2E7D32] font-semibold text-xs">Current</span> : <span className="text-[#B5AC9E] text-xs">Candidate</span>}</td>
            </tr>
          ))}
          {bls.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-[#B5AC9E]">No baselines captured.</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

const KV = ({ k, v }) => (
  <div className="flex justify-between items-center gap-2"><span className="text-[#8A8175]">{k}</span><span className="font-mono text-right truncate max-w-[200px]">{v}</span></div>
);
